// src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faUniversity,
  faHandHoldingUsd,
  faPiggyBank,
  faGem,
  faTicketAlt,
  faChartLine,
  faCogs,
  faSignOutAlt,
  faCreditCard,
  faReceipt,
  faBalanceScale,
  faBook,
  faFileAlt,
  faPhone,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import GeneralLoanHome from './GL/GeneralLoanHome';
import CDLoanHome from './CD/CDLoanHome';
import PimgyHome from './Pimgy/PimgyHome';
import ExpenseManager from './AdminDashboard/ExpenseManager';
import BalanceEnquiry from './AdminDashboard/BalanceEnquiry';
import AdminDailyCashbook from './AdminDashboard/AdminDailyCashbook';
import AssistantRegistrar from './AdminDashboard/AssistantRegistrar';
import SheetPage from './AdminDashboard/SheetPage';
import CallInfo from './AdminDashboard/CallInfo';
import LakshmikantDashboard, { LAKSHMIKANT_EMAIL } from './AdminDashboard/LakshmikantDashboard';
import './AdminDashboard.css';
import { API_BASE_URL } from '../config';
import { getTodayYYYYMMDD } from '../utils/dateUtils';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const adminEmail = (user?.email || localStorage.getItem('adminEmail') || '').toLowerCase().trim();
  const isLakshmikantUser = adminEmail === LAKSHMIKANT_EMAIL;
  const showLakshmikantDashboard =
    isLakshmikantUser &&
    (location.pathname === '/admin-dashboard' || location.pathname === '/admin-dashboard/');
  const [standardBalance, setStandardBalance] = useState(null);
  const [secondaryOpeningBalance, setSecondaryOpeningBalance] = useState(null);
  const [secondaryClosingBalance, setSecondaryClosingBalance] = useState(null);
  const [isLoadingStandard, setIsLoadingStandard] = useState(true);
  const [isLoadingSecondary, setIsLoadingSecondary] = useState(false);
  const [standardError, setStandardError] = useState('');
  const [secondaryError, setSecondaryError] = useState('');
  const [isSecondaryVisible, setIsSecondaryVisible] = useState(isLakshmikantUser);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const fetchStandardBalance = useCallback(async () => {
    setIsLoadingStandard(true);
    setStandardError('');
    try {
      const response = await fetch(`${API_BASE_URL}/balance-enquiry`);
      if (response.ok) {
        const data = await response.json();
        setStandardBalance(data.overallBalance ?? null);
      } else {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch overall balance.');
      }
    } catch (error) {
      console.error('Error fetching overall balance:', error);
      setStandardError(error.message || 'Unable to load overall balance.');
      setStandardBalance(null);
    } finally {
      setIsLoadingStandard(false);
    }
  }, []);

  const fetchSecondaryBalance = useCallback(async () => {
    setIsLoadingSecondary(true);
    setSecondaryError('');
    try {
      const today = getTodayYYYYMMDD();
      const response = await fetch(`${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${today}`);
      if (response.ok) {
        const data = await response.json();
        setSecondaryOpeningBalance(data.secondaryOpeningBalance ?? null);
        setSecondaryClosingBalance(data.secondaryClosingBalance ?? null);
      } else {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch secondary daily balances.');
      }
    } catch (error) {
      console.error('Error fetching secondary daily balances:', error);
      setSecondaryError(error.message || 'Unable to load secondary daily balances.');
      setSecondaryOpeningBalance(null);
      setSecondaryClosingBalance(null);
    } finally {
      setIsLoadingSecondary(false);
    }
  }, []);

  useEffect(() => {
    fetchStandardBalance();
  }, [fetchStandardBalance]);

  useEffect(() => {
    if (isLakshmikantUser) return undefined;

    const handleShortcut = (event) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'q') {
        event.preventDefault();
        setIsSecondaryVisible((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [isLakshmikantUser]);

  useEffect(() => {
    if (isLakshmikantUser) {
      setIsSecondaryVisible(true);
    }
  }, [isLakshmikantUser]);

  useEffect(() => {
    if (isSecondaryVisible) {
      fetchSecondaryBalance();
    } else if (!isLakshmikantUser) {
      setSecondaryOpeningBalance(null);
      setSecondaryClosingBalance(null);
      setSecondaryError('');
      setIsLoadingSecondary(false);
    }
  }, [isSecondaryVisible, fetchSecondaryBalance, isLakshmikantUser]);

  const handleLogout = () => {
    closeMobileMenu();
    logout();
    navigate('/WelcomePage');
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth > 768) return undefined;
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getBalanceColor = (balance) => {
    if (balance === null || balance === undefined) return '#6c757d';
    return balance >= 0 ? '#28a745' : '#dc3545';
  };

  return (
    <Box className="dashboard-container">
      <Box className="mobile-top-bar">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open navigation menu"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
        <Typography variant="h6" component="h1" className="mobile-top-bar-title">
          Admin Dashboard
        </Typography>
      </Box>

      {mobileMenuOpen && (
        <Box
          className="sidebar-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      <Box
        component="aside"
        className={`sidebar${mobileMenuOpen ? ' sidebar-open' : ''}`}
      >
        <button
          type="button"
          className="mobile-menu-close"
          onClick={closeMobileMenu}
          aria-label="Close navigation menu"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <Typography variant="h5" component="h2" className="sidebar-title">
          Admin Dashboard
        </Typography>
        <nav>
          <ul>
            <li>
              <NavLink to="/general-loan" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faMoneyBillWave} /> General Loan
              </NavLink>
            </li>
            <li>
              <NavLink to="/cd-loan" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faUniversity} /> CD Loan
              </NavLink>
            </li>
            <li>
              <NavLink to="/tbd-loan" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faHandHoldingUsd} /> TBD Loan
              </NavLink>
            </li>
            <li>
              <NavLink to="/fd" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faCreditCard} /> RD Component
              </NavLink>
            </li>
            <li>
              <NavLink to="/expense-manager" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faReceipt} /> Expense Manager
              </NavLink>
            </li>
            <li>
              <NavLink to="/balance-enquiry" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faBalanceScale} /> Balance Enquiry
              </NavLink>
            </li>
            <li>
              <NavLink to="/daily-cashbook" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faBook} /> Daily Cashbook
              </NavLink>
            </li>
            <li>
              <NavLink to="/pigmy-collection" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faPiggyBank} /> Pigmy Collection
              </NavLink>
            </li>
            <li>
              <NavLink to="/sheets" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faChartLine} /> Sheets
              </NavLink>
            </li>
            <li>
              <NavLink to="/assistant-registrar" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faFileAlt} /> Assistant Registrar
              </NavLink>
            </li>
            <li>
              <NavLink to="/call-info" activeClassName="active-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faPhone} /> Call Info
              </NavLink>
            </li>
          </ul>
        </nav>

        <Button
          variant="contained"
          color="primary"
          className="logout-button"
          onClick={handleLogout}
          startIcon={<FontAwesomeIcon icon={faSignOutAlt} />}
        >
          Logout
        </Button>
      </Box>

      <Box component="main" className="content">
        {/* Overall Balance Display */}
        <Box className="overall-balance-widget">
          <Typography variant="h6" className="balance-label">
            Overall Balance
          </Typography>
          {isLoadingStandard ? (
            <Typography 
              variant="h4" 
              className="balance-amount" 
              style={{ color: '#6c757d' }}
            >
              Loading...
            </Typography>
          ) : standardError ? (
            <Typography 
              variant="body1" 
              className="balance-amount" 
              style={{ color: '#dc3545' }}
            >
              {standardError}
            </Typography>
          ) : (
            <Typography 
              variant="h4" 
              className="balance-amount" 
              style={{ color: getBalanceColor(standardBalance) }}
            >
              {standardBalance !== null ? formatCurrency(standardBalance) : 'N/A'}
            </Typography>
          )}
        </Box>

        {isSecondaryVisible && (
          <Box className="overall-balance-widget secondary-daily-balances">
            <Typography variant="h6" className="balance-label">
              Secondary Balance (Today)
            </Typography>
            {isLoadingSecondary ? (
              <Typography
                variant="h4"
                className="balance-amount"
                style={{ color: '#6c757d' }}
              >
                Loading...
              </Typography>
            ) : secondaryError ? (
              <Typography
                variant="body1"
                className="balance-amount"
                style={{ color: '#dc3545' }}
              >
                {secondaryError}
              </Typography>
            ) : (
              <>
                <Box className="secondary-balance-row">
                  <Typography variant="subtitle1" className="balance-sublabel">
                    Opening Balance
                  </Typography>
                  <Typography
                    variant="h4"
                    className="balance-amount"
                    style={{ color: getBalanceColor(secondaryOpeningBalance) }}
                  >
                    {secondaryOpeningBalance !== null ? formatCurrency(secondaryOpeningBalance) : 'N/A'}
                  </Typography>
                </Box>
                <Box className="secondary-balance-row">
                  <Typography variant="subtitle1" className="balance-sublabel">
                    Closing Balance
                  </Typography>
                  <Typography
                    variant="h4"
                    className="balance-amount"
                    style={{ color: getBalanceColor(secondaryClosingBalance) }}
                  >
                    {secondaryClosingBalance !== null ? formatCurrency(secondaryClosingBalance) : 'N/A'}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {showLakshmikantDashboard && (
          <LakshmikantDashboard adminEmail={adminEmail} />
        )}

        <Routes>
          <Route path="/general-loan/*" element={<GeneralLoanHome />} />
          <Route path="/cd-loan" element={<CDLoanHome />} />
          <Route path="/tbd-loan" element={<Typography variant="h6">TBD Loan</Typography>} />
          <Route path="/expense-manager" element={<ExpenseManager />} />
          <Route path="/balance-enquiry" element={<BalanceEnquiry />} />
          <Route path="/daily-cashbook" element={<AdminDailyCashbook />} />
          <Route path="/pigmy-collection/*" element={<PimgyHome />} />
          <Route path="/assistant-registrar" element={<AssistantRegistrar />} />
          <Route path="/sheets" element={<SheetPage />} />
          <Route path="/call-info" element={<CallInfo />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
